import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { toObservable, toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  Validators,
  AbstractControl,
  ValidationErrors,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import {
  combineLatest,
  map,
  startWith,
  distinctUntilChanged,
  debounceTime,
  switchMap,
  of,
} from 'rxjs';
import { DatabaseService } from '../../../../core/services/database.service';
import { ServersService } from '../../../../core/services/servers.service';
import { WebsitesService } from '../../../../core/services/websites.service';
import { PasswordValidators } from '../../../../shared/components/validators/password.validators';
import { DatabaseUserCreate } from '../../../../shared/models/database.models';
import { AsyncPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { InputField } from '../../../../shared/components/input-field/input-field';

@Component({
  selector: 'app-add-database-users-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    InputField,
    AsyncPipe,
  ],
  templateUrl: './add-database-users-dialog.component.html',
  styleUrl: './add-database-users-dialog.component.scss',
})
export class AddDatabaseUsersDialogComponent implements OnInit {
  private readonly dialogRef = inject(
    MatDialogRef<AddDatabaseUsersDialogComponent, DatabaseUserCreate>,
  );

  private readonly destroyRef = inject(DestroyRef);

  private readonly fb = inject(FormBuilder);

  private readonly serversService = inject(ServersService);

  private readonly websitesService = inject(WebsitesService);

  private readonly databaseService = inject(DatabaseService);

  readonly serverSearchText = signal('');

  readonly websiteSearchText = signal<string | undefined>('');

  readonly form = this.fb.nonNullable.group(
    {
      server: ['', Validators.required],
      websiteId: [{ value: '', disabled: true }, Validators.required],
      databaseId: [{ value: '', disabled: true }, Validators.required],
      username: ['', [this.nameValidator]],
      password: [
        '',
        [
          PasswordValidators.required(),
          PasswordValidators.complexity(),
          PasswordValidators.allowedCharacters(),
          PasswordValidators.minLength(),
          PasswordValidators.maxLength(),
        ],
      ],
      passwordConf: ['', [PasswordValidators.required()]],
    },
    {
      validators: [PasswordValidators.match('password', 'passwordConf')],
    },
  );

  readonly servers$ = combineLatest([
    this.serversService.getAllServers(),
    toObservable(this.serverSearchText),
  ]).pipe(
    map(([servers, searchText]) =>
      servers
        .filter((server) => server.hostname.toLowerCase().includes(searchText.trim().toLowerCase()))
        .map((server) => ({
          label: server.hostname,
          value: server._id,
        })),
    ),
  );

  readonly websites$ = combineLatest([
    this.form.controls.server.valueChanges.pipe(
      startWith(this.form.controls.server.value),
      distinctUntilChanged(),
    ),
    toObservable(this.websiteSearchText).pipe(
      map((searchText) => searchText ?? ''),
      debounceTime(300),
      distinctUntilChanged(),
    ),
  ]).pipe(
    switchMap(([serverId, searchText]) => {
      if (!serverId) {
        return of([]);
      }

      return this.websitesService
        .searchWebsites({
          serverSearch: serverId,
          domainSearch: searchText.trim() || undefined,
        })
        .pipe(
          map((response) =>
            response.Websites.map((website) => ({
              label: website.domain,
              value: String(website.id),
            })),
          ),
        );
    }),
  );

  readonly websiteOptions = toSignal(this.websites$, {
    initialValue: [],
  });

  readonly databases$ = this.form.controls.websiteId.valueChanges.pipe(
    startWith(this.form.controls.websiteId.value),
    distinctUntilChanged(),
    switchMap((websiteId) => {
      if (!websiteId) {
        return of([]);
      }

      return this.databaseService.getByWebsiteId(Number(websiteId)).pipe(
        map((databases) =>
          databases
            .filter((database) => database.server === this.form.controls.server.value)
            .map((database) => ({
              label: database.name,
              value: String(database.id),
            })),
        ),
      );
    }),
  );

  readonly databaseOptions = toSignal(this.databases$, {
    initialValue: [],
  });

  ngOnInit(): void {
    this.form.controls.server.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((serverId) => {
        this.form.controls.websiteId.reset();
        this.form.controls.databaseId.reset();
        this.websiteSearchText.set('');

        if (serverId) {
          this.form.controls.websiteId.enable();
        } else {
          this.form.controls.websiteId.disable();
        }

        this.form.controls.databaseId.disable();
      });

    this.form.controls.websiteId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((websiteId) => {
        this.form.controls.databaseId.reset();

        if (websiteId) {
          this.form.controls.databaseId.enable();
        } else {
          this.form.controls.databaseId.disable();
        }
      });
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();

    const result: DatabaseUserCreate = {
      username: value.username,
      password: value.password,
      passwordConf: value.passwordConf,
      databaseId: Number(value.databaseId),
    };

    this.dialogRef.close(result);
  }

  close(): void {
    this.dialogRef.close();
  }

  private nameValidator(control: AbstractControl): ValidationErrors | null {
    const value: string = control.value ?? '';

    if (!value) {
      return { required: 'Name is required' };
    }

    if (value.length > 24) {
      return { maxLength: 'Name can be at most 24 characters' };
    }

    if (value.length < 3) {
      return { minLength: 'Name has to be at least 3 characters' };
    }

    if (!/^[a-z0-9_]+$/.test(value)) {
      return {
        invalidCharacters: 'Name must contain only lowercase letters, numbers and underscores',
      };
    }

    return null;
  }
}
